import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import {
  Car,
  Clock,
  DollarSign,
  Shield,
  Star,
  Users,
  CheckCircle,
  Mail,
  MapPin,
  Calendar,
  ChevronDown,
  Menu,
  X,
  Play,
  ArrowRight,
  FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/posts?limit=3`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  // Hourly Sprinter Rental Packages
  const hourlyPackages = [
    {
      hours: 4,
      price: 250,
      pricePerHour: 62.50,
      popular: false,
      features: ['Airport transfers', 'City tours', 'Short events']
    },
    {
      hours: 6,
      price: 225,
      pricePerHour: 37.50,
      popular: true,
      features: ['Corporate events', 'Wedding shuttles', 'Day trips']
    },
    {
      hours: 8,
      price: 200,
      pricePerHour: 25,
      popular: false,
      features: ['Full day service', 'Multi-stop tours', 'Special occasions']
    }
  ];

  const suvPackages = [
    {
      hours: 4,
      pricePerHour: 150,
      total: 600,
      popular: false,
      features: ['Airport transfers', 'Business meetings', 'City tours']
    },
    {
      hours: 6,
      pricePerHour: 125,
      total: 750,
      popular: true,
      features: ['Corporate events', 'Wedding transportation', 'Day trips']
    },
    {
      hours: 8,
      pricePerHour: 100,
      total: 800,
      popular: false,
      features: ['Full day service', 'Multi-city travel', 'Special occasions']
    }
  ];

  const features = [
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Book rides anytime, anywhere with our reliable service'
    },
    {
      icon: Shield,
      title: 'Safe & Secure',
      description: 'Verified drivers, real-time tracking, and insurance coverage'
    },
    {
      icon: DollarSign,
      title: 'Transparent Pricing',
      description: 'No hidden fees, see your price upfront before booking'
    },
    {
      icon: Star,
      title: 'Top-Rated Drivers',
      description: 'All drivers are rated by passengers for quality assurance'
    }
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Create Account',
      description: 'Sign up in seconds with your email and phone number',
      icon: Users
    },
    {
      step: '2',
      title: 'Book Your Ride',
      description: 'Enter pickup, dropoff, choose your vehicle, and select your time',
      icon: Calendar
    },
    {
      step: '3',
      title: 'Track Your Driver',
      description: 'See your driver\'s location in real-time on the map',
      icon: MapPin
    },
    {
      step: '4',
      title: 'Enjoy the Ride',
      description: 'Sit back and enjoy a comfortable, professional ride',
      icon: Car
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Regular Rider',
      rating: 5,
      text: 'Onyx Reserve has been my go-to for airport runs. Professional drivers, always on time, and the vehicles are so comfortable!'
    },
    {
      name: 'David Martinez',
      role: 'Corporate Client',
      rating: 5,
      text: 'I use Onyx Reserve for all my business meetings. The hourly Sprinter packages are perfect for our corporate events!'
    },
    {
      name: 'Emily Chen',
      role: 'Business Traveler',
      rating: 5,
      text: 'Perfect for corporate travel. The real-time tracking gives me peace of mind, and the service is consistently excellent.'
    }
  ];

  const faqs = [
    {
      question: 'How much does a ride cost?',
      answer: 'Pricing varies based on distance and time. Enter your pickup and dropoff locations when booking to see the exact price. We also offer discounted hourly Sprinter packages: 4 hours at $250, 6 hours at $225, or 8 hours at $200.'
    },
    {
      question: 'What vehicles do you offer?',
      answer: 'We offer Black Suburban SUVs, Black Sprinter vans, and the brand new 2026 Cadillac Escalade for a premium, comfortable experience. Perfect for individuals, families, corporate events, or groups up to 8 passengers.'
    },
    {
      question: 'How do I pay?',
      answer: 'We accept all major credit cards and PayPal through our secure payment system. Payment is processed when you book your ride.'
    },
    {
      question: 'Can I book a Sprinter for multiple hours?',
      answer: 'Yes! We offer 4, 6, and 8-hour rental packages for our Black Sprinter. The longer you book, the more you save per hour. Perfect for weddings, corporate events, and group outings.'
    },
    {
      question: 'Is there a cancellation policy?',
      answer: 'You can cancel up to 24 hours before your scheduled ride for a full refund. Cancellations within 24 hours may be subject to a fee.'
    },
    {
      question: 'Do you offer airport pickup?',
      answer: 'Yes! Airport pickup is one of our most popular services. Just provide your flight number, and we\'ll track your flight and adjust pickup time if needed.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-sm z-50 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="/onyx-reserve-logo.png" 
                alt="Onyx Reserve" 
                className="h-[72px] w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('fleet')} className="text-gray-300 hover:text-white">Fleet</button>
              <button onClick={() => scrollToSection('packages')} className="text-gray-300 hover:text-white">Hourly Packages</button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-300 hover:text-white">How It Works</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-300 hover:text-white">FAQ</button>
              <button onClick={() => navigate('/blog')} className="text-gray-300 hover:text-white">Blog</button>
              <Button variant="outline" className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black" onClick={() => navigate('/login')}>Login</Button>
              <Button className="bg-gold-500 text-black hover:bg-gold-400" onClick={() => navigate('/register?role=rider')}>Book Now</Button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-800">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('fleet')} className="text-gray-300 hover:text-white py-2">Fleet</button>
                <button onClick={() => scrollToSection('packages')} className="text-gray-300 hover:text-white py-2">Hourly Packages</button>
                <button onClick={() => scrollToSection('how-it-works')} className="text-gray-300 hover:text-white py-2">How It Works</button>
                <button onClick={() => scrollToSection('faq')} className="text-gray-300 hover:text-white py-2">FAQ</button>
                <button onClick={() => navigate('/blog')} className="text-gray-300 hover:text-white py-2">Blog</button>
                <Button variant="outline" className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black w-full" onClick={() => navigate('/login')}>Login</Button>
                <Button className="bg-gold-500 text-black hover:bg-gold-400 w-full" onClick={() => navigate('/register?role=rider')}>Book Now</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Premium <span className="text-blue-600">Black Car Service</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Experience premium transportation with Onyx Reserve. Professional drivers, luxury Black Car Service, and flexible booking options for every journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => navigate('/register?role=rider')} className="text-lg px-8">
                  Book a Ride
                </Button>
                <Button size="lg" variant="outline" onClick={() => scrollToSection('packages')} className="text-lg px-8">
                  View Hourly Packages
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-6">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-gold-400 fill-current" />
                  <span className="ml-2 text-gray-600 font-semibold">4.9/5 Rating</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="ml-2 text-gray-600 font-semibold">10,000+ Rides</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_9f390971-c0d4-4d2a-a399-34c5105bc66f/artifacts/qzo5rung_IMG_2559.jpeg" 
                alt="Onyx Reserve Luxury Suburban"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">100% Safe</div>
                    <div className="text-sm text-gray-600">Verified Drivers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury SUV Hourly Packages Section */}
      <section id="suv-packages" className="py-16 px-4 sm:px-6 lg:px-8 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Luxury SUV Hourly Packages
            </h2>
            <p className="text-lg text-gray-300">
              Premium luxury SUV service - perfect for executive travel, airport transfers, and special occasions
            </p>
            <p className="text-gold-400 mt-2 font-semibold">
              4-Hour Minimum Required
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {suvPackages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden bg-gray-900 border-gray-700 ${pkg.popular ? 'border-2 border-gold-500 shadow-xl scale-105' : 'hover:shadow-lg'} transition-all`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gold-500 text-black text-center py-1 text-sm font-semibold">
                    BEST VALUE
                  </div>
                )}
                <CardContent className={`pt-${pkg.popular ? '10' : '6'}`}>
                  <div className="text-center">
                    <div className="inline-block bg-gold-500 text-black px-4 py-1 rounded-full text-sm font-bold mb-4">
                      {pkg.hours} Hours
                    </div>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-white">${pkg.pricePerHour}</span>
                      <span className="text-gray-400">/hour</span>
                    </div>
                    <p className="text-gold-400 font-semibold mb-6">
                      Total: ${pkg.total}
                    </p>
                    
                    <ul className="text-left space-y-3 mb-6">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-gold-500 flex-shrink-0" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-gold-500 flex-shrink-0" />
                        <span className="text-gray-300">Up to 6 passengers</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-gold-500 flex-shrink-0" />
                        <span className="text-gray-300">Professional chauffeur</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-gold-500 flex-shrink-0" />
                        <span className="text-gray-300">Luxury leather interior</span>
                      </li>
                    </ul>
                    
                    <Button 
                      className={`w-full ${pkg.popular ? 'bg-gold-500 hover:bg-gold-400 text-black' : 'bg-white hover:bg-gray-100 text-black'}`}
                      onClick={() => navigate(`/book-suv?hours=${pkg.hours}&total=${pkg.total}`)}
                    >
                      Book {pkg.hours} Hours - ${pkg.total}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-gray-800 px-6 py-3 rounded-full">
              <Car className="w-5 h-5 text-gold-500" />
              <span className="text-gray-300">Premium Luxury SUV Fleet</span>
            </div>
          </div>
          
          <p className="text-center text-gray-400 mt-6">
            * Fuel and gratuity not included. Secure payment link sent upon booking confirmation.
          </p>
        </div>
      </section>

      {/* Fleet Section - Sprinter */}
      <section id="fleet" className="py-16 px-4 sm:px-6 lg:px-8 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Our Premium Fleet
            </h2>
            <p className="text-lg text-gray-300">
              Luxury vehicles for every occasion
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sprinter Image */}
            <div className="order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_9f390971-c0d4-4d2a-a399-34c5105bc66f/artifacts/y91ys1uo_Screen%20Shot%202026-01-10%20at%205.16.59%20PM.png" 
                alt="Black Sprinter Van"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            
            {/* Sprinter Info */}
            <div className="order-1 lg:order-2">
              <h3 className="text-3xl font-bold mb-4">Black Sprinter Van</h3>
              <p className="text-gray-300 text-lg mb-6">
                Perfect for group travel, corporate events, weddings, and special occasions. 
                Accommodates up to 8 passengers in luxury comfort.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Up to 8 passengers</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Leather seating & climate control</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>WiFi & USB charging</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Professional chauffeur</span>
                </li>
              </ul>
              <Button 
                size="lg" 
                onClick={() => scrollToSection('packages')}
                className="bg-white text-black hover:bg-gray-100"
              >
                View Hourly Packages
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Section - Escalade */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Escalade Info */}
            <div>
              <div className="inline-block px-4 py-1 bg-gold-500/20 rounded-full mb-4">
                <span className="text-gold-400 font-semibold text-sm">NEW ADDITION</span>
              </div>
              <h3 className="text-3xl font-bold mb-4">2026 Cadillac Escalade</h3>
              <p className="text-gray-300 text-lg mb-6">
                The ultimate in luxury transportation. Experience Miami in style with our brand new 
                Cadillac Escalade - the pinnacle of automotive luxury and sophistication.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-gold-400" />
                  <span>Up to 6 passengers</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-gold-400" />
                  <span>Premium leather interior</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-gold-400" />
                  <span>38" OLED curved display</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-gold-400" />
                  <span>Executive rear seating</span>
                </li>
              </ul>
              <div className="bg-black/50 rounded-xl p-6 mb-8">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-bold text-white">$150</span>
                  <span className="text-gray-400">/hour</span>
                </div>
                <p className="text-gray-400 text-sm">Minimum 3 hours</p>
              </div>
              <Button 
                size="lg" 
                onClick={() => navigate('/contact')}
                className="bg-gold-500 text-black hover:bg-gold-400 font-semibold"
              >
                Book Escalade Now
              </Button>
            </div>
            
            {/* Escalade Image */}
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/a3873035-d7c6-43c1-ae72-fcc38a42473a/images/53563f0abb7b35156f795881a3b2b298cc338501633ddd3506373d8b1eb08838.png" 
                alt="2026 Cadillac Escalade with Miami Skyline"
                className="rounded-2xl shadow-2xl w-full"
              />
              <p className="text-center text-gray-500 text-sm mt-3">Miami's skyline never looked better</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              See Us In Action
            </h2>
            <p className="text-lg text-gray-300">
              Experience the Onyx Reserve difference
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <video 
              controls 
              className="w-full"
              poster="https://customer-assets.emergentagent.com/job_9f390971-c0d4-4d2a-a399-34c5105bc66f/artifacts/y91ys1uo_Screen%20Shot%202026-01-10%20at%205.16.59%20PM.png"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_9f390971-c0d4-4d2a-a399-34c5105bc66f/artifacts/ajxawz2h_7d179dd2-6d8e-4e06-a1f7-764ad2571848.mov" 
                type="video/quicktime" 
              />
              <source 
                src="https://customer-assets.emergentagent.com/job_9f390971-c0d4-4d2a-a399-34c5105bc66f/artifacts/ajxawz2h_7d179dd2-6d8e-4e06-a1f7-764ad2571848.mov" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Hourly Packages Section */}
      <section id="packages" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Black Sprinter Hourly Packages
            </h2>
            <p className="text-lg text-gray-600">
              Book our luxury Sprinter by the hour - perfect for events, tours, and group travel
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {hourlyPackages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden ${pkg.popular ? 'border-2 border-blue-600 shadow-xl scale-105' : 'hover:shadow-lg'} transition-all`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-blue-600 text-white text-center py-1 text-sm font-semibold">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className={`pt-${pkg.popular ? '10' : '6'}`}>
                  <div className="text-center">
                    <div className="inline-block bg-black text-white px-4 py-1 rounded-full text-sm mb-4">
                      {pkg.hours} Hours
                    </div>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-gray-900">${pkg.price}</span>
                      <span className="text-gray-500">/hour</span>
                    </div>
                    <p className="text-green-600 font-semibold mb-6">
                      Total: ${pkg.hours * pkg.price}
                    </p>
                    
                    <ul className="text-left space-y-3 mb-6">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-600">Up to 8 passengers</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-600">Professional chauffeur</span>
                      </li>
                    </ul>
                    
                    <Button 
                      className={`w-full ${pkg.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      onClick={() => navigate('/register?role=rider')}
                    >
                      Book {pkg.hours} Hours
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <p className="text-center text-gray-500 mt-8">
            * Prices shown are per hour. Fuel and gratuity not included. 
            <br />Contact us for custom packages or longer bookings.
          </p>
        </div>
      </section>

      {/* Drive Your Car Section */}
      <section id="drive-your-car" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Info */}
            <div>
              <div className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Car className="w-4 h-4" />
                DESIGNATED DRIVER SERVICE
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Drive Your Car
              </h2>
              <p className="text-lg text-gray-300 mb-6">
                Had a few drinks? Need a ride but want to keep your car? Our professional drivers 
                will drive YOU home in YOUR own vehicle. No need to leave your car behind or take an Uber!
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="bg-gray-800/50 rounded-lg p-4 border-l-4 border-purple-500">
                  <h4 className="font-semibold text-white mb-1">🍸 Drive You Home</h4>
                  <p className="text-sm text-gray-400">We pick you up and drive you home in YOUR car</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border-l-4 border-blue-500">
                  <h4 className="font-semibold text-white mb-1">🚗 Take Your Car Home</h4>
                  <p className="text-sm text-gray-400">Keep partying - we'll take your car home safely</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border-l-4 border-green-500">
                  <h4 className="font-semibold text-white mb-1">🎯 Personal Chauffeur</h4>
                  <p className="text-sm text-gray-400">Use your own car instead of Uber - we'll drive you around</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-400">$60</div>
                  <div className="text-sm text-gray-400">per hour</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-400">$0.60</div>
                  <div className="text-sm text-gray-400">per mile</div>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => navigate('/drive-your-car')}
                className="bg-purple-600 hover:bg-purple-500 text-white px-8"
              >
                Book a Driver Now
              </Button>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-2xl p-8">
                <div className="bg-gray-800 rounded-xl p-6 shadow-2xl">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Why Choose Us?
                  </h3>
                  <ul className="space-y-4 text-sm">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-white">Keep Your Car</span>
                        <p className="text-gray-400">No need to leave it at the bar or pay for parking</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-white">Professional Drivers</span>
                        <p className="text-gray-400">Licensed, insured, and background-checked</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-white">Any Vehicle</span>
                        <p className="text-gray-400">Standard, luxury, or exotic - we handle it all</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-white">Safe & Responsible</span>
                        <p className="text-gray-400">Drive safe, arrive home with your car</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Onyx Reserve?
            </h2>
            <p className="text-lg text-gray-600">
              Premium service that puts you first
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition">
                <CardContent className="pt-6">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Book your premium ride in 4 simple steps
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition h-full">
                  <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                    <step.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <div className="w-8 h-0.5 bg-blue-300"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              What Our Riders Say
            </h2>
            <p className="text-lg text-gray-300">
              Join thousands of satisfied customers
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-gold-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t border-gray-700 pt-4">
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {blogPosts.length > 0 && (
        <section id="blog" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Latest from Our Blog
              </h2>
              <p className="text-xl text-gray-600">
                Tips, insights, and news about luxury transportation in Miami
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                  {post.featured_image && (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    {post.category && (
                      <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">
                        {post.category}
                      </span>
                    )}
                    <h3 className="text-xl font-bold mt-2 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      <Link to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {(post.excerpt || post.meta_description || '').replace(/<[^>]*>/g, '')}
                    </p>
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800"
                    >
                      Read More <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/blog')}
                className="border-2"
              >
                <FileText className="w-5 h-5 mr-2" />
                View All Articles
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Experience Premium Transportation?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Book your Onyx Reserve today and travel in style
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => navigate('/register?role=rider')}
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8"
            >
              Book a Ride Now
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => navigate('/drivers')}
              className="border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8"
            >
              Become a Driver
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-white p-2 rounded-lg">
                  <Car className="w-6 h-6 text-gray-900" />
                </div>
                <span className="text-xl font-bold">Onyx Reserve</span>
              </div>
              <p className="text-gray-400">
                Premium black car service for all your transportation needs.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('fleet')} className="text-gray-400 hover:text-white">Our Fleet</button></li>
                <li><button onClick={() => scrollToSection('packages')} className="text-gray-400 hover:text-white">Hourly Packages</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')} className="text-gray-400 hover:text-white">How It Works</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-gray-400 hover:text-white">FAQ</button></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Airport Transfers</li>
                <li>Corporate Events</li>
                <li>Wedding Transportation</li>
                <li>City Tours</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  info@onyxreserve.net
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Onyx Reserve. All rights reserved.</p>
            <p className="text-sm mt-3 text-gray-300">
              Powered by MMTA, INC – Licensed & Insured Black Car and Limousine Company
            </p>
            <p className="text-xs mt-2 text-gray-500">
              Driver & Concierge Commission: Drivers receive 80% of ride fares. Concierges earn 20% commission on rides they refer. Payouts processed every Monday.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
